// External Libraries
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Styles
import '../styles/Dashboard.css';

// Redux/State Management
import { RootState } from '../redux/store';

// API Services
import { getAllIdeasFromAPI } from '../api/IdeaService';

// Types
import { IdeaListResponse, Idea } from '../types/types';

// Utilities
import { formatDate, getSupportNames } from '../utils/CommonUtils';

// Components
import IdeaCard from './common/IdeaCard';
import Loading from './common/Loading';
import Pagination from './common/Pagination';
import Text from './common/Text';
import Button from './common/Button';

const IdeasFollowing: React.FC = () => {
  const navigate = useNavigate();
  const userId = useSelector(
    (state: RootState) => state.user.userDetails?.userId,
  );
  const reduxSeekings = useSelector(
    (state: RootState) => state.support_or_partnership,
  ).support_or_partnership;
  const reduxDepartments = useSelector((state: RootState) => state.departments);
  const reduxSchools = useSelector((state: RootState) => state.schools);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [noIdeasFound, setNoIdeasFound] = useState<boolean>(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNumber = queryParams.get('page');
  const initialPage = pageNumber ? Number(pageNumber) : 1;
  const [currentPage, setCurrentPage] = useState<number>(Number(initialPage));

  useEffect(() => {
    if (!pageNumber) {
      setCurrentPage(initialPage);
    }
  }, [pageNumber, initialPage]);

  const pageSize = 10;

  useEffect(() => {
    const fetchFollowingIdeas = async () => {
      if (userId) {
        await fetchIdeasData(currentPage);
        // window.scrollTo(0, 0);
      }
    };
    fetchFollowingIdeas();
  }, [currentPage, userId]);

  const fetchIdeasData = async (page: number) => {
    try {
      setLoading(true);
      if (userId) {
        const result = await getAllIdeasFromAPI(
          userId,
          page,
          pageSize,
          undefined,
          true,
          undefined,
          undefined,
          undefined,
        );

        if ('code' in result) {
          if (result.code === 'NOT_FOUND') {
            if (currentPage > 1) {
              setCurrentPage(1);
              handlePageChange(1);
            } else {
              setNoIdeasFound(true);
            }
          } else if (result.code === 'BAD_REQUEST') {
            setMessage('No Following ideas found');
            setNoIdeasFound(false);
          } else if (
            result.code === 'FORBIDDEN' ||
            result.code === 'UNAUTHORIZED'
          ) {
            setMessage(result.message);
            setNoIdeasFound(false);
          } else {
            setMessage(result.message);
            setNoIdeasFound(false);
          }
        } else {
          const { ideas: ideasResponse, total_pages } =
            result as IdeaListResponse;
          if (ideasResponse.length > 0) {
            setIdeas(ideasResponse);
            setTotalPages(total_pages);
            setMessage(null);
          } else {
            setMessage('No following ideas found on this page');
          }
          setNoIdeasFound(false);
        }
      } else {
        setMessage('User is not logged in.');
      }
    } catch (error) {
      console.error('Catch Error fetching ideas:', error);
      setMessage('Failed to fetch ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowChange = () => {
    fetchIdeasData(currentPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    navigate(`/following?page=${page}`);
    // window.scrollTo(0, 0);
    setLoading(true);
  };

  return (
    <>
      {loading && (
        <div className="loading-class">
          <Loading color="var(--black)" />
        </div>
      )}

      {message && <div className="loading-class">{message}</div>}

      {noIdeasFound && (
        <div className="loading-class">
          <div className="centered-container p-20 grid gap-20">
            <Text className="primary-text">
              Stay Connected with Ideas You’re Following
            </Text>
            <Text className="secondary-text">
              This is where you&apos;ll find updates and progress on projects
              that interest you. Follow an idea to stay in the loop and
              contribute to its journey.
            </Text>
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="tertiary-button"
            >
              Explore New Ideas to Follow
            </Button>
          </div>
        </div>
      )}

      {!loading && !noIdeasFound && message === null && (
        <div className="grid gap-10">
          <div className="ideas-page-header">
            <Text className="display-extra-small">Ideas</Text>
          </div>
          {ideas.length > 0 &&
            ideas.map((idea: Idea) => (
              <IdeaCard
                id={idea.id}
                title={idea.title as string}
                ideaOwner={`${idea.principal_investigator.first_name} ${idea.principal_investigator.last_name}`}
                ideaOwnerPhoto={idea.principal_investigator.profile_image_url}
                postedDate={idea.created_at && formatDate(idea.created_at)}
                key={idea.id}
                isMyIdea={false}
                schoolName={
                  reduxSchools.schools.find(
                    (s) => s.id === idea.principal_investigator.school_id,
                  )?.name ?? ''
                }
                departmentName={
                  reduxDepartments.departments.find(
                    (d) => d.id === idea.principal_investigator.department_id,
                  )?.name ?? ''
                }
                isFollowed={idea.is_following}
                ideaOwnerId={idea.principal_investigator.id}
                onFollowChange={handleFollowChange}
                seeking={
                  idea.support_ids?.length
                    ? getSupportNames(idea.support_ids, reduxSeekings)
                    : []
                }
              />
            ))}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {!loading && message && (
        <div className="loading-class">
          No following ideas found on this page. Please try a different page or
          filter.
        </div>
      )}
    </>
  );
};

export default IdeasFollowing;
